import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogActions } from '@angular/material/dialog';
import { map, Observable } from 'rxjs';
import { BaseComponentTypeEnum, AttributeType } from '@tailormap-viewer/api';
import { FilterConditionEnum, FilterTypeEnum, AttributeFilterModel } from '@tailormap-viewer/api';
import { SimpleAttributeFilterService } from '../../../filter/services/simple-attribute-filter.service';
import { AttributeFilterHelper, AttributeFilterComponent } from '@tailormap-viewer/shared';
import { AttributeListManagerService } from '../services/attribute-list-manager.service';
import { MatButton } from '@angular/material/button';

export interface FilterDialogData {
  tabSourceId: string;
  columnName: string;
  layerId: string;
  filter: AttributeFilterModel | null;
  columnType: AttributeType;
  cqlFilters?: Map<string | symbol, string>;
  applicationId: string;
  attributeAlias?: string;
  featureType?: string;
}

interface FilterType {
  condition?: FilterConditionEnum;
  value?: string[];
  caseSensitive?: boolean;
  invertCondition?: boolean;
}

@Component({
    selector: 'tm-attribute-list-filter',
    templateUrl: './attribute-list-filter.component.html',
    styleUrls: ['./attribute-list-filter.component.css'],
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatDialogTitle,
        AttributeFilterComponent,
        MatDialogActions,
        MatButton,
    ],
})
export class AttributeListFilterComponent implements OnInit {

  public uniqueValues$: Observable<string[]> | null = null;
  public filter: FilterType = {};
  public updatedFilter: FilterType = {};

  private simpleAttributeFilterService = inject(SimpleAttributeFilterService);
  private dialogRef = inject(MatDialogRef<AttributeListFilterComponent>);
  private data: FilterDialogData = inject(MAT_DIALOG_DATA);
  private managerService = inject(AttributeListManagerService);

  public ngOnInit(): void {
    this.uniqueValues$ = this.getUniqueValues$();
    if (!this.data.filter) {
      return;
    }
    this.filter = {
      condition: this.data.filter.condition,
      value: this.data.filter.value,
      caseSensitive: this.data.filter.caseSensitive,
      invertCondition: this.data.filter.invertCondition,
    };
    this.updatedFilter = { ...this.filter };
  }

  public onOk() {
    const filter = this.getFilter();
    if (!AttributeFilterHelper.isValidFilter(filter)) {
      return;
    }
    this.simpleAttributeFilterService.setFilter(BaseComponentTypeEnum.ATTRIBUTE_LIST, this.data.layerId, filter);
    this.dialogRef.close();
  }

  public onCancel() {
    this.dialogRef.close();
  }

  public onClear() {
    this.simpleAttributeFilterService.removeFilter(BaseComponentTypeEnum.ATTRIBUTE_LIST, this.data.layerId, this.data.columnName);
    this.dialogRef.close();
  }

  public getUniqueValues$(): Observable<string[]> {
    return this.managerService.getUniqueValues$(this.data.tabSourceId, {
      attribute: this.data.columnName,
      layerId: this.data.layerId,
      filter: this.data.cqlFilters,
      applicationId: this.data.applicationId,
    })
      .pipe(
        map(response => {
          return response.values.map(v => `${v}`) || [];
        }),
      );
  }

  public updateFilter(filter: {
    condition: FilterConditionEnum;
    value: string[];
    caseSensitive?: boolean;
    invertCondition?: boolean;
  }) {
    this.updatedFilter = filter;
  }

  public getAttributeType() {
    return this.data.columnType;
  }

  public getColumnName() {
    return this.data.columnName;
  }

  public isValidFilter() {
    return AttributeFilterHelper.isValidFilter(this.getFilter());
  }

  private getFilter(): Omit<AttributeFilterModel, 'id'> | null {
    if (!this.updatedFilter.condition) {
      return null;
    }
    return {
      type: FilterTypeEnum.ATTRIBUTE,
      condition: this.updatedFilter.condition,
      value: typeof this.updatedFilter.value === 'undefined' ? [] : this.updatedFilter.value,
      attribute: this.data.columnName,
      attributeType: this.data.columnType,
      caseSensitive: typeof this.updatedFilter.caseSensitive === 'boolean'
        ? this.updatedFilter.caseSensitive
        : false,
      invertCondition: typeof this.updatedFilter.invertCondition === 'boolean'
        ? this.updatedFilter.invertCondition
        : false,
      attributeAlias: this.data.attributeAlias,
      featureType: this.data.featureType,
    };
  }

}
